import { boundedMemo } from './boundedMemo.js'

const ET_PART_FORMATTER = new Intl.DateTimeFormat('en-US', {
  timeZone: 'America/New_York',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
})

function normalizeDate(input) {
  if (input instanceof Date) return Number.isNaN(input.getTime()) ? null : input
  const d = new Date(input)
  return Number.isNaN(d.getTime()) ? null : d
}

const minuteParts = boundedMemo(minute => {
  const parts = ET_PART_FORMATTER.formatToParts(new Date(minute * 60_000))
  const get = (type) => parts.find((p) => p.type === type)?.value
  const hourRaw = Number(get('hour'))
  return {
    year: Number(get('year')),
    month: Number(get('month')),
    day: Number(get('day')),
    hour: hourRaw === 24 ? 0 : hourRaw,
    minute: Number(get('minute')),
  }
}, 256)

function getEtParts(input) {
  const date = normalizeDate(input)
  return date ? minuteParts(Math.floor(date.getTime() / 60_000)) : null
}

function dateKey(year, month, day) {
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
}

function addDaysToKey(key, days) {
  const y = Number(key.slice(0, 4))
  const month = Number(key.slice(5, 7))
  const day = Number(key.slice(8, 10))
  const d = new Date(Date.UTC(y, month - 1, day + days))
  return dateKey(d.getUTCFullYear(), d.getUTCMonth() + 1, d.getUTCDate())
}

function nthWeekdayOfMonth(year, month, weekday, nth) {
  const first = new Date(Date.UTC(year, month - 1, 1)).getUTCDay()
  const day = 1 + ((weekday - first + 7) % 7) + (nth - 1) * 7
  return dateKey(year, month, day)
}

function lastWeekdayOfMonth(year, month, weekday) {
  const lastDate = new Date(Date.UTC(year, month, 0))
  const lastDay = lastDate.getUTCDate()
  const lastDow = lastDate.getUTCDay()
  const day = lastDay - ((lastDow - weekday + 7) % 7)
  return dateKey(year, month, day)
}

function observedFixedDateKey(year, month, day) {
  const dow = new Date(Date.UTC(year, month - 1, day)).getUTCDay()
  if (dow === 6) return addDaysToKey(dateKey(year, month, day), -1)
  if (dow === 0) return addDaysToKey(dateKey(year, month, day), 1)
  return dateKey(year, month, day)
}

function easterDateKey(year) {
  const a = year % 19
  const b = Math.floor(year / 100)
  const c = year % 100
  const d = Math.floor(b / 4)
  const e = b % 4
  const f = Math.floor((b + 8) / 25)
  const g = Math.floor((b - f + 1) / 3)
  const h = (19 * a + b - d - g + 15) % 30
  const i = Math.floor(c / 4)
  const k = c % 4
  const l = (32 + 2 * e + 2 * i - h - k) % 7
  const m = Math.floor((a + 11 * h + 22 * l) / 451)
  const month = Math.floor((h + l - 7 * m + 114) / 31)
  const day = ((h + l - 7 * m + 114) % 31) + 1
  return dateKey(year, month, day)
}

const defaultNyseHolidayKeys = boundedMemo(year => {
  // Standard full-day US equity closures. Early-close days are still
  // trading days and intentionally stay out of this set.
  const holidays = new Set([
    // NYSE does not observe a Saturday New Year's Day on December 31.
    new Date(Date.UTC(year, 0, 1)).getUTCDay() === 6 ? dateKey(year, 1, 1) : observedFixedDateKey(year, 1, 1),
    nthWeekdayOfMonth(year, 1, 1, 3),
    nthWeekdayOfMonth(year, 2, 1, 3),
    addDaysToKey(easterDateKey(year), -2),
    lastWeekdayOfMonth(year, 5, 1),
    observedFixedDateKey(year, 6, 19),
    observedFixedDateKey(year, 7, 4),
    nthWeekdayOfMonth(year, 9, 1, 1),
    nthWeekdayOfMonth(year, 11, 4, 4),
    observedFixedDateKey(year, 12, 25),
  ])
  return holidays
}, 8)

function readHolidaySet(tradingCalendar) {
  const raw = tradingCalendar?.holidays
  if (!Array.isArray(raw) || raw.length === 0) return null
  const out = new Set()
  for (const h of raw) {
    if (typeof h === 'string') out.add(h)
    else if (h && typeof h.date === 'string') out.add(h.date)
  }
  return out.size ? out : null
}

const standardNyseEarlyCloseKeys = boundedMemo(year => {
  const keys = new Set()

  // NYSE's recurring 1 p.m. ET closes: the Friday after Thanksgiving,
  // plus July 3 / Christmas Eve when the holiday falls Tuesday-Friday.
  const thanksgiving = nthWeekdayOfMonth(year, 11, 4, 4)
  keys.add(addDaysToKey(thanksgiving, 1))

  const independenceDayDow = new Date(Date.UTC(year, 6, 4)).getUTCDay()
  if (independenceDayDow >= 2 && independenceDayDow <= 5) {
    keys.add(dateKey(year, 7, 3))
  }

  const christmasDow = new Date(Date.UTC(year, 11, 25)).getUTCDay()
  if (christmasDow >= 2 && christmasDow <= 5) {
    keys.add(dateKey(year, 12, 24))
  }

  return keys
}, 8)

export function getEtMarketDateKey(date = new Date()) {
  const et = getEtParts(date)
  return et ? dateKey(et.year, et.month, et.day) : null
}

export function isUsEquityTradingDay(date = new Date(), tradingCalendar = null) {
  const et = getEtParts(date)
  if (!et) return false
  const key = dateKey(et.year, et.month, et.day)
  const dow = new Date(Date.UTC(et.year, et.month - 1, et.day)).getUTCDay()
  if (dow === 0 || dow === 6) return false
  if (defaultNyseHolidayKeys(et.year).has(key)) return false
  const holidays = readHolidaySet(tradingCalendar)
  if (holidays?.has(key)) return false
  return true
}

export function isUsEquityEarlyCloseDay(date = new Date(), tradingCalendar = null) {
  const et = getEtParts(date)
  if (!et || !isUsEquityTradingDay(date, tradingCalendar)) return false
  return standardNyseEarlyCloseKeys(et.year).has(dateKey(et.year, et.month, et.day))
}

/**
 * Returns the current US equity market session based on Eastern Time.
 * Pre-market: 4:00-9:30 AM ET | Regular: 9:30 AM-4:00 PM ET | Post: 4:00-8:00 PM ET
 * @returns {'pre' | 'regular' | 'post' | 'closed'}
 */
export function getMarketSession(date = new Date(), tradingCalendar = null) {
  const et = getEtParts(date)
  if (!et) return 'closed'
  if (!isUsEquityTradingDay(date, tradingCalendar)) return 'closed'

  const time = et.hour * 60 + et.minute
  const regularClose = isUsEquityEarlyCloseDay(date, tradingCalendar) ? 780 : 960
  if (time >= 240 && time < 570) return 'pre'
  if (time >= 570 && time < regularClose) return 'regular'
  if (time >= regularClose && time < 1200) return 'post'
  return 'closed'
}

export function isExtendedHours(date = new Date(), tradingCalendar = null) {
  const session = getMarketSession(date, tradingCalendar)
  return session === 'pre' || session === 'post'
}
